import apiClient from './client';
import { apiFailure } from './errors';
import type {
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateInvitationRequest,
  UpdateMemberRoleRequest,
  OrganizationResponse,
  OrganizationsListResponse,
  OrganizationDetailResponse,
  MembersListResponse,
  InvitationsListResponse,
  InvitationResponse,
  MembershipResponse,
  SimpleResponse,
  OrganizationWithMembership,
  OrganizationDetail,
  Member,
  Invitation,
  Organization,
  Membership,
  AuditLogsResponse,
  AuditStatsResponse,
  CriticalActionsResponse,
  LoginActivityResponse,
  AuditLogQueryParams,
  CriticalActionsQueryParams,
  LoginActivityQueryParams,
  OrgAuditLog,
  CriticalActionLogEntry,
  LoginAttemptEntry,
} from '@/types/organization';
import type { CriticalActionSignature } from '@/types/criticalAction';

interface ApiResponse<T> {
  status: string;
  data: T;
}

export const organizationsApi = {
  // ============================================================
  // Organization CRUD
  // ============================================================

  createOrganization: async (data: CreateOrganizationRequest): Promise<Organization> => {
    const response = await apiClient.post<ApiResponse<OrganizationResponse>>(
      '/organizations',
      data
    );
    if (!response.data.data.success || !response.data.data.organization) {
      throw apiFailure(response.data.data, 'Failed to create organization');
    }
    return response.data.data.organization;
  },

  getOrganizations: async (): Promise<OrganizationWithMembership[]> => {
    const response = await apiClient.get<ApiResponse<OrganizationsListResponse>>('/organizations');
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get organizations');
    }
    return response.data.data.organizations || [];
  },

  getOrganization: async (orgId: string): Promise<OrganizationDetail> => {
    const response = await apiClient.get<ApiResponse<OrganizationDetailResponse>>(
      `/organizations/${orgId}`
    );
    if (!response.data.data.success || !response.data.data.organization) {
      throw apiFailure(response.data.data, 'Failed to get organization');
    }
    return response.data.data.organization;
  },

  updateOrganization: async (
    orgId: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization> => {
    const response = await apiClient.patch<ApiResponse<OrganizationResponse>>(
      `/organizations/${orgId}`,
      data
    );
    if (!response.data.data.success || !response.data.data.organization) {
      throw apiFailure(response.data.data, 'Failed to update organization');
    }
    return response.data.data.organization;
  },

  deleteOrganization: async (orgId: string, signature: CriticalActionSignature): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleResponse>>(
      `/organizations/${orgId}`,
      { data: signature }
    );
    const payload = response.data.data;
    if (!payload.success) {
      const err = new Error(payload.error || 'Failed to delete organization') as Error & {
        errorCode?: string;
      };
      if (payload.errorCode) err.errorCode = payload.errorCode;
      throw err;
    }
  },

  // ============================================================
  // Members
  // ============================================================

  getMembers: async (orgId: string): Promise<Member[]> => {
    const response = await apiClient.get<ApiResponse<MembersListResponse>>(
      `/organizations/${orgId}/members`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get members');
    }
    return response.data.data.members || [];
  },

  // memberId can be wkIdentity, email, or membership id
  updateMemberRole: async (
    orgId: string,
    memberId: string,
    data: UpdateMemberRoleRequest,
    signature?: CriticalActionSignature
  ): Promise<Membership> => {
    const encodedMemberId = encodeURIComponent(memberId);
    const response = await apiClient.patch<ApiResponse<MembershipResponse>>(
      `/organizations/${orgId}/members/${encodedMemberId}`,
      { ...data, ...signature }
    );
    if (!response.data.data.success || !response.data.data.membership) {
      throw apiFailure(response.data.data, 'Failed to update member role');
    }
    return response.data.data.membership;
  },

  // memberId can be wkIdentity, email, or membership id
  removeMember: async (
    orgId: string,
    memberId: string,
    signature: CriticalActionSignature
  ): Promise<void> => {
    const encodedMemberId = encodeURIComponent(memberId);
    const response = await apiClient.delete<ApiResponse<SimpleResponse>>(
      `/organizations/${orgId}/members/${encodedMemberId}`,
      { data: signature }
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to remove member');
    }
  },

  getMemberVaultRoles: async (
    orgId: string,
    memberId: string
  ): Promise<{ vaultId: string; vaultName: string; role: string; chain: string }[]> => {
    const encodedMemberId = encodeURIComponent(memberId);
    const response = await apiClient.get<
      ApiResponse<{
        success: boolean;
        vaultRoles?: { vaultId: string; vaultName: string; role: string; chain: string }[];
      }>
    >(`/organizations/${orgId}/members/${encodedMemberId}/vault-roles`);
    return response.data.data.vaultRoles || [];
  },

  leaveOrganization: async (orgId: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleResponse>>(
      `/organizations/${orgId}/leave`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to leave organization');
    }
  },

  // ============================================================
  // Organization Invitations
  // ============================================================

  createInvitation: async (orgId: string, data: CreateInvitationRequest): Promise<Invitation> => {
    const response = await apiClient.post<ApiResponse<InvitationResponse>>(
      `/organizations/${orgId}/invitations`,
      data
    );
    if (!response.data.data.success || !response.data.data.invitation) {
      throw apiFailure(response.data.data, 'Failed to create invitation');
    }
    return response.data.data.invitation;
  },

  getOrgInvitations: async (orgId: string): Promise<Invitation[]> => {
    const response = await apiClient.get<ApiResponse<InvitationsListResponse>>(
      `/organizations/${orgId}/invitations`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get invitations');
    }
    return response.data.data.invitations || [];
  },

  revokeInvitation: async (orgId: string, invitationId: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<SimpleResponse>>(
      `/organizations/${orgId}/invitations/${invitationId}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to revoke invitation');
    }
  },

  // ============================================================
  // User Invitations
  // ============================================================

  getMyInvitations: async (): Promise<Invitation[]> => {
    const response = await apiClient.get<ApiResponse<InvitationsListResponse>>('/invitations');
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get invitations');
    }
    return response.data.data.invitations || [];
  },

  acceptInvitation: async (invitationId: string): Promise<Membership> => {
    const response = await apiClient.post<ApiResponse<MembershipResponse>>(
      `/invitations/${invitationId}/accept`
    );
    if (!response.data.data.success || !response.data.data.membership) {
      throw apiFailure(response.data.data, 'Failed to accept invitation');
    }
    return response.data.data.membership;
  },

  rejectInvitation: async (invitationId: string): Promise<void> => {
    const response = await apiClient.post<ApiResponse<SimpleResponse>>(
      `/invitations/${invitationId}/reject`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to reject invitation');
    }
  },

  // ============================================================
  // Organization Activity & Analytics
  // ============================================================

  getAuditLogs: async (
    orgId: string,
    params?: AuditLogQueryParams
  ): Promise<{ logs: OrgAuditLog[]; total: number }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<AuditLogsResponse>>(
      `/organizations/${orgId}/audit-logs${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get audit logs');
    }
    return { logs: response.data.data.logs || [], total: response.data.data.total || 0 };
  },

  getAuditStats: async (
    orgId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<NonNullable<AuditStatsResponse['stats']>> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<AuditStatsResponse>>(
      `/organizations/${orgId}/audit-logs/stats${qs}`
    );
    if (!response.data.data.success || !response.data.data.stats) {
      throw apiFailure(response.data.data, 'Failed to get audit stats');
    }
    return response.data.data.stats;
  },

  getCriticalActions: async (
    orgId: string,
    params?: CriticalActionsQueryParams
  ): Promise<{
    logs: CriticalActionLogEntry[];
    total: number;
    stats: CriticalActionsResponse['stats'];
  }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<CriticalActionsResponse>>(
      `/organizations/${orgId}/critical-actions${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get critical actions');
    }
    return {
      logs: response.data.data.logs || [],
      total: response.data.data.total || 0,
      stats: response.data.data.stats,
    };
  },

  getLoginActivity: async (
    orgId: string,
    params?: LoginActivityQueryParams
  ): Promise<{
    attempts: LoginAttemptEntry[];
    total: number;
    stats: LoginActivityResponse['stats'];
  }> => {
    const qs = buildQueryString(params);
    const response = await apiClient.get<ApiResponse<LoginActivityResponse>>(
      `/organizations/${orgId}/login-activity${qs}`
    );
    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Failed to get login activity');
    }
    return {
      attempts: response.data.data.attempts || [],
      total: response.data.data.total || 0,
      stats: response.data.data.stats,
    };
  },
};

function buildQueryString(params?: object): string {
  if (!params) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export default organizationsApi;
